'use client';

/**
 * "Open in your browser to sign in": the sheet the login screen shows when it
 * finds itself inside another app's embedded browser (2026-09-25, the Revent
 * mobile first touch, design section 2.4). Why it exists and how the web view
 * is recognised is in lib/auth/in-app-browser.ts; this file is only the surface.
 *
 * WHAT IS ON IT, in the order a person in a loud room needs it:
 *
 *   1. One sentence naming the app whose browser this is and the browser to
 *      open instead. No jargon: "web view" and "user agent" stay in the code.
 *   2. The direct way out where the platform has one: `intent://` to Chrome on
 *      Android, `x-safari-https://` on iOS. An ordinary anchor, so the in-app
 *      browser's own URL handling gets to act on it.
 *   3. The page's FULL link in a copy box with a Copy button, `#fragment`
 *      included, because the fragment can be a live arrival code and the whole
 *      reason the person is here. `navigator.clipboard` first; when it is
 *      absent or refused (older web views) the box is selected, so a
 *      long-press copy is one gesture away. No share sheet: `navigator.share`
 *      is not delegated to this surface, and the design forbids promising one.
 *   4. The other ways in that work here: every provider of the screen but
 *      Google (X, Discord, LinkedIn where it is configured), with the same
 *      hrefs the page below draws, so the sheet never grows a second copy of
 *      the provider list (lib/auth/sign-in-providers.ts is the one list).
 *   5. "Try here anyway". Nothing is blocked: the sheet is a warning with the
 *      tools on it, and dismissing it (the link, the X, the backdrop, Escape)
 *      shows the ordinary screen with Google on it.
 *
 * Same shape as the host install sheet (components/app-host/install-sheet.tsx):
 * a bottom sheet on a phone, a centred card from `md` up.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, X } from 'lucide-react';
import { useTranslation } from '@/components/i18n-provider';
import { ProviderIcon } from '@/components/provider-icon';
import {
  IN_APP_BROWSER_NAMES,
  inAppEscapeBrowser,
  inAppEscapeHref,
  type InAppBrowserDetection,
} from '@/lib/auth/in-app-browser';
import { SIGN_IN_PROVIDER_INFO, type SignInProvider } from '@/lib/auth/sign-in-providers';

export interface InAppBrowserAlternative {
  slug: SignInProvider;
  href: string;
  label: string;
}

export function InAppBrowserSheet({
  detection,
  href,
  alternatives,
  onProvider,
  onDismiss,
}: {
  detection: InAppBrowserDetection;
  /** The page's own URL, fragment included. Shown in the box and handed to the escape. */
  href: string;
  /** Providers that work inside a web view, drawn with the page's own hrefs. */
  alternatives: InAppBrowserAlternative[];
  onProvider: (slug: InAppBrowserAlternative['slug']) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  const browser = inAppEscapeBrowser(detection.os);
  const browserName = browser ?? t('auth.inAppYourBrowser');
  const escape = inAppEscapeHref(href, detection.os);
  const appName = detection.app ? IN_APP_BROWSER_NAMES[detection.app] : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const copy = useCallback(async () => {
    // Read the address at click time: the fragment is the part that matters
    // and the part a stale render would be most likely to miss.
    const text = (typeof window !== 'undefined' && window.location.href) || href;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        return;
      }
    } catch {
      /* refused or unavailable: fall through to the selection */
    }
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
    try {
      if (document.execCommand('copy')) setCopied(true);
    } catch {
      /* the text stays selected for a manual copy */
    }
  }, [href]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="login-in-app-sheet"
      className="fixed inset-0 z-[240] flex items-end justify-center bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={onDismiss} />
      <div className="glass-panel relative flex w-full max-h-[90vh] flex-col overflow-hidden rounded-t-2xl shadow-2xl md:w-[min(420px,96vw)] md:rounded-2xl">
        <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
          <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h2 id={titleId} className="min-w-0 flex-1 truncate text-sm font-semibold">
            {t('auth.inAppTitle')}
          </h2>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('auth.inAppClose')}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 overflow-y-auto px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm">
          <p className="text-muted-foreground" data-testid="login-in-app-body">
            {appName
              ? t('auth.inAppBodyNamed', { app: appName, browser: browserName })
              : t('auth.inAppBodyUnnamed', { browser: browserName })}
          </p>

          {escape && browser ? (
            <a
              href={escape}
              data-testid="login-in-app-open"
              className="btn-primary w-full justify-center flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              {t('auth.inAppOpenIn', { browser })}
            </a>
          ) : null}

          <div>
            <label htmlFor={inputId} className="sr-only">
              {t('auth.inAppLinkLabel')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id={inputId}
                ref={inputRef}
                type="text"
                readOnly
                value={href}
                onFocus={(e) => e.currentTarget.select()}
                data-testid="login-in-app-link"
                className="min-w-0 flex-1 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
              />
              <button
                type="button"
                onClick={copy}
                data-testid="login-in-app-copy"
                className="btn-outline shrink-0 flex items-center gap-1.5"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('auth.inAppCopied') : t('auth.inAppCopyLink')}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t('auth.inAppCopyHint', { browser: browserName })}
            </p>
          </div>

          {alternatives.length > 0 ? (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-muted-foreground">{t('auth.inAppAlternatives')}</p>
              {alternatives.map((alt) => (
                <a
                  key={alt.slug}
                  href={alt.href}
                  onClick={() => onProvider(alt.slug)}
                  data-testid={`login-in-app-alt-${alt.slug}`}
                  className="btn-outline w-full justify-center flex items-center gap-2"
                >
                  <ProviderIcon slug={SIGN_IN_PROVIDER_INFO[alt.slug].iconSlug} size={16} />
                  {alt.label}
                </a>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            onClick={onDismiss}
            data-testid="login-in-app-try-anyway"
            className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground transition-colors"
          >
            {t('auth.inAppTryAnyway')}
          </button>
        </div>
      </div>
    </div>
  );
}
